import _ from 'lodash';
import React from 'react';

import Select from 'design-system/SelectInput';
import { makeStyles } from 'react-scale/components/design-system';
import {
  LabelChoice,
  isNestedLabelChoice,
  getLabelChoiceValue,
  getLabelChoiceHint,
  getLabelChoiceDisplay,
} from '@scale/shared/types/params/annotation';
import HintTooltipIcon from '../../tasks/textcollection/HintTooltipIcon';
import { TextField, MenuItem } from 'react-scale/components/design-system/product';

export interface AttributeSelectTreeProps {
  choices: LabelChoice[];
  selectedChoice?: string;
  handleSelect: (choice: string) => void;
  isReadOnly?: boolean;
  description?: string;
  isPrelabel?: boolean;
}

type MenuItemArray = (JSX.Element | MenuItemArray)[];

export const AttributeSelectTree: React.FC<AttributeSelectTreeProps> = ({
  choices,
  selectedChoice,
  handleSelect,
  isReadOnly,
  description,
  isPrelabel,
}) => {
  const classes = useStyles();
  const onChange = (e: React.ChangeEvent<HTMLSelectElement>) => handleSelect(e.target.value);

  const renderSubtree = (subchoices: LabelChoice[], level: number) => {
    return _.map(subchoices, c => {
      const choiceValue = getLabelChoiceValue(c);
      const choiceDisplay = getLabelChoiceDisplay(c);
      if (isNestedLabelChoice(c)) {
        return (
          <React.Fragment key={choiceValue}>
            <option disabled key={choiceValue}>
              {_.times(1 * level, _.constant('—'))} {choiceDisplay}
            </option>
            {renderSubtree(c.subchoices, level + 1)}
          </React.Fragment>
        );
      } else {
        return (
          <option key={choiceValue} value={choiceValue}>
            {_.times(1 * level, _.constant('—'))} {choiceDisplay}
          </option>
        );
      }
    });
  };

  const renderSubtreeArray = (subchoices: LabelChoice[], level: number): MenuItemArray => {
    return _.map(subchoices, c => {
      const choiceValue = getLabelChoiceValue(c);
      const choiceDisplay = getLabelChoiceDisplay(c);
      const choiceHint = getLabelChoiceHint(c);
      if (isNestedLabelChoice(c)) {
        return [
          <MenuItem disabled key={choiceValue}>
            {_.times(1 * level, _.constant('—'))} {choiceDisplay}
            {choiceHint && <HintTooltipIcon key={choiceValue} hint={choiceHint} />}
          </MenuItem>,
          renderSubtreeArray(c.subchoices, level + 1),
        ];
      } else {
        return (
          <MenuItem key={choiceValue} value={choiceValue}>
            {_.times(1 * level, _.constant('—'))} {choiceDisplay}
            {choiceHint && <HintTooltipIcon key={choiceValue} hint={choiceHint} />}
          </MenuItem>
        );
      }
    });
  };

  return description === undefined ? (
    <Select
      disabled={isReadOnly}
      value={selectedChoice ?? ''}
      onChange={onChange}
      blurOnChange
      fullWidth
    >
      <option key="default" value="" />
      {renderSubtree(choices, 0)}
    </Select>
  ) : (
    <TextField
      InputProps={{
        classes: { root: isPrelabel ? classes.prelabelAttributeRoot : classes.attributeRoot },
      }}
      disabled={isReadOnly}
      value={selectedChoice ?? ''}
      onChange={e => handleSelect(e.target.value as string)}
      select
      label={description.split(' ').join('\xa0')}
      variant="outlined"
      size="small"
      onKeyDown={e => {
        e.stopPropagation();
      }}
      fullWidth
    >
      {[<MenuItem key="default" value=""></MenuItem>, renderSubtreeArray(choices, 0)]}
    </TextField>
  );
};

const useStyles = makeStyles(() => ({
  attributeRoot: {
    background: 'white',
  },
  prelabelAttributeRoot: {
    background: 'rgba(0, 255, 0, 0.1)',
  },
}));
